import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { getUnsignedAgreements, signAgreements } from '@/api/backend/service/agreement';
import {
  type AuthDeliveryType,
  generateLoginCode,
  getIsFillProfile,
  verifyLoginCode
} from '@/api/backend/service/auth';
import { apiGtm, mapAuthDeliveryToGTMMethod, type GTMAuthContext } from '@/api/gtm';
import { InvisibleCaptcha, useInvisibleCaptcha } from '@/api/yandex-captcha';
import { IconKitArrow } from '@/icon/kit/Arrow';
import { UiKitButton } from '@/ui-kit/button';
import { UiKitInputOTP } from '@/ui-kit/input-otp';
import { toastBase } from '@/ui-kit/toast';
import { utilFormatPhoneNumber } from '@/util/format-phone-number';

import { OauthMethods } from '../OauthMethods';

import { styles } from './AuthCodeForm.styles';

const SMS_REQUEST_TIMEOUT = 10;

const formatSmsTimer = (secs: number) => {
  const m = Math.floor(secs / 60).toString();
  const s = (secs % 60).toString().padStart(2, '0');

  return `${m}:${s}`;
};

interface AuthCodeFormProps {
  phone: string;
  placement: 'header' | 'cart';
  redirectUrl?: string;
  authSessionId: string;
  authContext: GTMAuthContext;
  onSuccess: (needsRegistration: boolean) => Promise<boolean>;
  onBack: () => void;
}

export function AuthCodeForm({
  phone,
  placement,
  redirectUrl,
  authSessionId,
  authContext,
  onSuccess,
  onBack
}: AuthCodeFormProps) {
  const t = useTranslations();

  const slots = styles();
  const { captchaKey, captchaProps, requestToken } = useInvisibleCaptcha();

  const [deliveryType, setDeliveryType] = useState<AuthDeliveryType>('call');
  const [showOtherAuth, setShowOtherAuth] = useState(false);
  const [isRequestingSms, setIsRequestingSms] = useState(false);
  const [smsRequestSecondsLeft, setSmsRequestSecondsLeft] = useState(SMS_REQUEST_TIMEOUT);
  const attemptNumberRef = useRef(0);

  const isCall = deliveryType === 'call';
  const codeLength = isCall ? 4 : 6;
  const authMethod = mapAuthDeliveryToGTMMethod(deliveryType);

  useEffect(() => {
    if (!isCall || smsRequestSecondsLeft <= 0) return;

    const timer = setInterval(() => {
      setSmsRequestSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isCall, smsRequestSecondsLeft]);

  const handleSendCode = async (otpCode: string) => {
    attemptNumberRef.current += 1;

    apiGtm.authCodeSubmit({
      auth_session_id: authSessionId,
      auth_method: authMethod,
      attempt_number: attemptNumberRef.current
    });

    const response = await verifyLoginCode({
      otpCode,
      phone
    });

    if (response.isError) {
      setShowOtherAuth(true);

      return {
        status: 'error' as const,
        message: response.requestError?.error?.message ?? ''
      };
    }

    const { data: unsignedIds } = await getUnsignedAgreements();
    if (unsignedIds?.length) {
      await signAgreements({ version_ids: unsignedIds });
      apiGtm.signUp({
        placement,
        method: 'phone'
      });
    }

    apiGtm.login({
      placement,
      method: 'phone'
    });

    const fillProfile = await getIsFillProfile();
    const needsRegistration =
      fillProfile.isError || !fillProfile.data || !fillProfile.data.isFillRequired;

    await onSuccess(needsRegistration);

    return {
      status: 'success' as const,
      message: 'Code verified successfully'
    };
  };

  const handleResendCode = async () => {
    let captchaToken: string;

    try {
      captchaToken = await requestToken();
    } catch {
      return;
    }

    const response = await generateLoginCode({
      phone,
      recaptchaToken: captchaToken,
      deliveryType
    });

    if (response.isError) {
      toastBase(response.requestError?.error.message ?? t('login.requestCodeError'), {
        status: 'error'
      });

      throw new Error(
        response.requestError?.error.message ?? t('login.requestCodeError')
      );
    }

    apiGtm.authCodeSent({
      auth_session_id: authSessionId,
      auth_method: authMethod,
      provider: authMethod
    });
  };

  const handleRequestSms = async () => {
    if (isRequestingSms || smsRequestSecondsLeft > 0) return;

    setIsRequestingSms(true);

    apiGtm.authAlternativeSwitch({
      auth_session_id: authSessionId,
      auth_context: authContext
    });

    try {
      let captchaToken: string;

      try {
        captchaToken = await requestToken();
      } catch {
        return;
      }

      const response = await generateLoginCode({
        phone,
        recaptchaToken: captchaToken,
        deliveryType: 'sms'
      });

      if (response.isError) {
        toastBase(response.requestError?.error.message ?? t('login.requestCodeError'), {
          status: 'error'
        });
        return;
      }

      setDeliveryType('sms');
      setShowOtherAuth(false);
      attemptNumberRef.current = 0;

      apiGtm.authCodeSent({
        auth_session_id: authSessionId,
        auth_method: 'sms',
        provider: 'sms'
      });
    } finally {
      setIsRequestingSms(false);
    }
  };

  return (
    <div>
      <div>
        <UiKitButton
          color="ghost"
          onClick={onBack}
        >
          <IconKitArrow
            direction="right"
            className={slots.arrow()}
          />
        </UiKitButton>
      </div>
      <h2 className={slots.title()}>{t('login.code.title')}</h2>

      <div className={slots.numberBlock()}>
        <div className={slots.number()}>{utilFormatPhoneNumber(phone)}</div>
        <button
          type="button"
          className={slots.numberChange()}
          onClick={onBack}
        >
          {t('change')}
        </button>
      </div>

      <div className={slots.helperText()}>
        {isCall ? t('login.code.helperTextCall') : t('login.code.helperText')}
      </div>

      <div>
        <div className={slots.otpContainer()}>
          <UiKitInputOTP
            key={deliveryType}
            maxLength={codeLength}
            onSendCode={handleSendCode}
            onResend={handleResendCode}
            showResend={!isCall}
            afterInput={
              isCall ? (
                <div className={slots.requestSmsBlock()}>
                  {smsRequestSecondsLeft > 0 && (
                    <span className={slots.requestSmsTimer()}>
                      {t('login.code.requestSmsIn', {
                        time: formatSmsTimer(smsRequestSecondsLeft)
                      })}
                    </span>
                  )}
                  <button
                    type="button"
                    className={slots.requestSms()}
                    onClick={handleRequestSms}
                    disabled={isRequestingSms || smsRequestSecondsLeft > 0}
                  >
                    {t('login.code.requestSms')}
                  </button>
                </div>
              ) : undefined
            }
          />
        </div>
        <InvisibleCaptcha
          key={captchaKey}
          visible={captchaProps.visible}
          onSuccess={captchaProps.onSuccess}
          onChallengeVisible={captchaProps.onChallengeVisible}
          onChallengeHidden={captchaProps.onChallengeHidden}
          onNetworkError={captchaProps.onNetworkError}
        />
      </div>
      <div className={slots.support()}>
        <div>{t('login.code.support')}</div>
        {/* TODO: Выводить телефон из конфига */}
        <Link
          href="tel:88005118420"
          className={slots.supportPhone()}
          target="_blank"
          rel="noopener noreferrer"
        >
          8 800 511-84-20
        </Link>
      </div>
      {showOtherAuth && (
        <OauthMethods
          placement={placement}
          redirectUrl={redirectUrl}
        />
      )}
    </div>
  );
}
